import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getAccounts } from "../selectors/skedCoreSelectors";
import { AccountType } from "../shared/types/accounts";
import {
  isPausedAccount,
  isLinkedInNewPermissionRequired,
  isTiktokNewPermissionRequired,
  doesAccountNeedApiReconnect,
  isInstagramPhoneStateIgnored,
  type AccountStatusOptions,
} from "../entities/accounts/libs/get-account-statuses";
import { useInstagramApiPublishingOn } from "../utils/feature-toggle-utils";
import { isLiAccountNeedUpdate } from "../entities/accounts/components/account/avatar";

const DISMISS_STORAGE_KEY = "sked_reconnect_dialog_dismissed_until";
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export type ReconnectionReason =
  | "failed"
  | "paused"
  | "promptApiReconnect"
  | "linkedinScopes"
  | "tiktokScopes"
  | "missingScopes";

export interface AccountNeedingReconnection {
  account: AccountType;
  reasons: ReconnectionReason[];
}

/**
 * Checks if an account needs reconnection and returns the reasons why.
 */
export function getAccountReconnectionReasons(
  account: AccountType,
  options: Required<Pick<AccountStatusOptions, "instagramApiPublishing">>
): ReconnectionReason[] {
  if (account.isMarketingDemo) return [];
  const reasons: ReconnectionReason[] = [];

  // Check if account is paused (special case of failed)
  if (isPausedAccount(account)) {
    reasons.push("paused");
    return reasons; // Paused is exclusive
  }

  // Check if account has failed status. SKE-5376: on the Instagram API
  // track a failed phone login is not a reason to reconnect.
  if (
    account.status === "failed" &&
    !isInstagramPhoneStateIgnored(account, options.instagramApiPublishing)
  ) {
    reasons.push("failed");
  }

  // Check if API token needs reconnection (Instagram Insights)
  if (account.promptApiReconnect) {
    reasons.push("promptApiReconnect");
  }

  // Check LinkedIn scope requirements
  if (
    isLinkedInNewPermissionRequired(account) ||
    isLiAccountNeedUpdate(account)
  ) {
    reasons.push("linkedinScopes");
  }

  // Check TikTok scope requirements
  if (isTiktokNewPermissionRequired(account)) {
    reasons.push("tiktokScopes");
  }

  // Check for missing scopes (FB/IG inbox, insights, etc.)
  if (doesAccountNeedApiReconnect(account, options)) {
    reasons.push("missingScopes");
  }

  return reasons;
}

/**
 * Check if the dialog was dismissed within the last week.
 */
export function isDismissedForWeek(): boolean {
  try {
    const dismissedUntil = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!dismissedUntil) return false;

    const dismissedUntilTime = parseInt(dismissedUntil, 10);
    return Date.now() < dismissedUntilTime;
  } catch {
    return false;
  }
}

/**
 * Dismiss the dialog for one week.
 */
export function dismissForOneWeek(): void {
  try {
    const dismissUntil = Date.now() + ONE_WEEK_MS;
    localStorage.setItem(DISMISS_STORAGE_KEY, dismissUntil.toString());
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Clear the dismissal (useful for testing or when user reconnects an account).
 */
export function clearDismissal(): void {
  try {
    localStorage.removeItem(DISMISS_STORAGE_KEY);
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Hook to get accounts that need reconnection.
 * Returns accounts with their reconnection reasons.
 */
export function useAccountsNeedingReconnection(): {
  accountsNeedingReconnection: AccountNeedingReconnection[];
  hasAccountsNeedingReconnection: boolean;
  isDismissed: boolean;
} {
  const accounts = useSelector(getAccounts) as AccountType[];
  const instagramApiPublishing = useInstagramApiPublishingOn();

  const accountsNeedingReconnection = useMemo(() => {
    if (!accounts || accounts.length === 0) return [];

    const result: AccountNeedingReconnection[] = [];

    for (const account of accounts) {
      const reasons = getAccountReconnectionReasons(account, {
        instagramApiPublishing,
      });
      if (reasons.length > 0) {
        result.push({ account, reasons });
      }
    }

    return result;
  }, [accounts, instagramApiPublishing]);

  const isDismissed = isDismissedForWeek();

  return {
    accountsNeedingReconnection,
    hasAccountsNeedingReconnection: accountsNeedingReconnection.length > 0,
    isDismissed,
  };
}
